// frontend/src/pages/DataWorkspacePage/ConnectionsSidebar.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';
import AddConnectionModal from './AddConnectionModal.jsx';

/**
 * Left sidebar — shows all active DB connections, and expanding each
 * one reveals its schemas / tables (fetched once from /schema endpoint).
 */

const ENGINE_META = {
    1:          { icon: 'Database',    color: 'text-blue-400',   label: 'PostgreSQL' },
    2:          { icon: 'Database',    color: 'text-orange-400', label: 'MySQL'      },
    3:          { icon: 'DatabaseZap', color: 'text-cyan-400',   label: 'MSSQL'      },
    postgresql: { icon: 'Database',    color: 'text-blue-400',   label: 'PostgreSQL' },
    mysql:      { icon: 'Database',    color: 'text-orange-400', label: 'MySQL'      },
    mssql:      { icon: 'DatabaseZap', color: 'text-cyan-400',   label: 'MSSQL'      },
};

const ConnectionsSidebar = ({ token, onSelectTable, activeSelection, onSchemaLoaded }) => {
    const { addNotification } = useNotification();
    const [connections, setConnections]   = useState([]);
    const [schemas, setSchemas]           = useState({});         // { [connId]: { tables: [] } }
    const [expanded, setExpanded]         = useState({});         // { [connId]: bool }
    const [expandedTables, setExpandedTables] = useState({});     // { [connId_tableName]: bool }
    const [loadingSchema, setLoadingSchema] = useState({});       // { [connId]: bool }
    const [showAddModal, setShowAddModal] = useState(false);
    const [search, setSearch]             = useState('');

    // helper for column icons based on sql type
    const getColIcon = (typeStr) => {
        if (!typeStr) return 'AlignLeft';
        const t = typeStr.toLowerCase();
        if (t.includes('int') || t.includes('float') || t.includes('decimal') || t.includes('numeric')) return 'Hash';
        if (t.includes('date') || t.includes('time')) return 'Calendar';
        if (t.includes('bool') || t.includes('bit')) return 'ToggleLeft';
        return 'Type';
    };

    // -----------------------------------------------------------------------
    // Load connections
    // -----------------------------------------------------------------------
    const loadConnections = useCallback(async () => {
        try {
            const data = await apiFetch('/datasources', token);
            setConnections(data || []);
        } catch (err) {
            addNotification(`Failed to load connections: ${err.message}`, 'error');
        }
    }, [token, addNotification]);

    useEffect(() => { loadConnections(); }, [loadConnections]);

    // -----------------------------------------------------------------------
    // Load schema for a connection (lazy)
    // -----------------------------------------------------------------------
    const loadSchema = useCallback(async (connId) => {
        if (schemas[connId] || loadingSchema[connId]) return;
        setLoadingSchema(prev => ({ ...prev, [connId]: true }));
        try {
            const data = await apiFetch(`/datasources/${connId}/structure`, token);
            setSchemas(prev => ({ ...prev, [connId]: data }));
            if (onSchemaLoaded) onSchemaLoaded(connId, data);
        } catch (err) {
            addNotification(`Schema load failed: ${err.message}`, 'error');
        } finally {
            setLoadingSchema(prev => ({ ...prev, [connId]: false }));
        }
    }, [token, schemas, loadingSchema, addNotification]);

    const toggleConn = (conn) => {
        const next = !expanded[conn.id];
        setExpanded(prev => ({ ...prev, [conn.id]: next }));
        if (next) loadSchema(conn.id);
    };

    // -----------------------------------------------------------------------
    // Test connection
    // -----------------------------------------------------------------------
    const testConnection = async (e, conn) => {
        e.stopPropagation();
        try {
            const res = await apiFetch(`/datasources/test`, token, { 
                method: 'POST',
                body: JSON.stringify({ id: conn.id, name: conn.name, type: conn.type || conn.engine || 1 })
            });
            if (res.success || res.message === "Connection successful") {
                addNotification('Connection healthy ✓', 'success');
            } else {
                addNotification(`Connection failed: ${res.message}`, 'error');
            }
            loadConnections(); // refresh last_test_ok badge
        } catch (err) {
            addNotification(`Test failed: ${err.message}`, 'error');
        }
    };

    // -----------------------------------------------------------------------
    // Filter tables by search
    // -----------------------------------------------------------------------
    const filterTables = (tables) => {
        if (!search) return tables;
        return tables.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
    };

    return (
        <div className="w-64 shrink-0 flex flex-col bg-slate-50 border-r border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="px-4 pt-4 pb-3 border-b border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <DynamicIcon name="ServerCog" className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-semibold text-slate-800">Connections</span>
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-white text-blue-600 border border-slate-200 hover:bg-slate-100 transition-colors shadow-sm"
                    >
                        <DynamicIcon name="Plus" className="w-3 h-3" />
                        New
                    </button>
                </div>
                {/* Search */}
                <div className="relative">
                    <DynamicIcon name="Search" className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search tables..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-7 pr-3 py-1.5 rounded-md text-xs bg-white border border-slate-200 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm"
                    />
                </div>
            </div>

            {/* Connection List */}
            <div className="flex-1 overflow-y-auto py-2 space-y-0.5 px-2">
                {connections.length === 0 && (
                    <div className="p-4 text-center text-xs text-slate-500 italic">
                        No connections yet.<br />Click "New" to add one.
                    </div>
                )}
                {connections.map(conn => {
                    const meta    = ENGINE_META[conn.type] || ENGINE_META[conn.engine] || ENGINE_META.postgresql;
                    const isOpen  = expanded[conn.id];
                    const schema  = schemas[conn.id];
                    const loading = loadingSchema[conn.id];
                    const tables  = schema ? filterTables(schema.tables) : [];

                    return (
                        <div key={conn.id}>
                            {/* Connection row */}
                            <button
                                onClick={() => toggleConn(conn)}
                                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white transition-colors group text-left"
                            >
                                <DynamicIcon name={isOpen ? 'ChevronDown' : 'ChevronRight'} className="w-3 h-3 text-slate-400 shrink-0" />
                                <DynamicIcon name={meta.icon} className={`w-4 h-4 ${meta.color} shrink-0`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-slate-800 truncate">{conn.name}</p>
                                    <p className="text-[10px] text-slate-500">{meta.label}</p>
                                </div>
                                {/* Health dot */}
                                <span
                                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                        conn.last_test_ok === true  ? 'bg-emerald-500' :
                                        conn.last_test_ok === false ? 'bg-red-500' :
                                        'bg-slate-300'
                                    }`}
                                    title={conn.last_test_ok === true ? 'Connected' : conn.last_test_ok === false ? 'Failed' : 'Untested'}
                                />
                                <button
                                    onClick={(e) => testConnection(e, conn)}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-blue-600 text-slate-400 transition-all"
                                    title="Test connection"
                                >
                                    <DynamicIcon name="Zap" className="w-3 h-3" />
                                </button>
                            </button>

                            {/* Tables */}
                            {isOpen && (
                                <div className="ml-6 mt-0.5 space-y-0.5 mb-1">
                                    {loading && (
                                        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-500">
                                            <DynamicIcon name="Loader2" className="w-3 h-3 animate-spin" />
                                            Loading schema...
                                        </div>
                                    )}
                                    {!loading && tables.map(tbl => {
                                        const isActive = activeSelection?.connectionId === conn.id && activeSelection?.tableName === tbl.name;
                                        const tableKey = `${conn.id}_${tbl.name}`;
                                        const isTableOpen = expandedTables[tableKey];
                                        return (
                                            <div key={tbl.name}>
                                                <div className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors ${
                                                    isActive ? 'bg-blue-50 text-blue-700' : 'hover:bg-white text-slate-600'
                                                }`}>
                                                    <button 
                                                        onClick={() => setExpandedTables(prev => ({ ...prev, [tableKey]: !prev[tableKey] }))}
                                                        className="p-0.5 hover:bg-slate-200 rounded text-slate-400 shrink-0"
                                                    >
                                                        <DynamicIcon name={isTableOpen ? 'ChevronDown' : 'ChevronRight'} className="w-3 h-3" />
                                                    </button>
                                                    
                                                    <button
                                                        draggable
                                                        onDragStart={(e) => {
                                                            e.dataTransfer.setData('application/json', JSON.stringify({
                                                                type: 'table',
                                                                connectionId:   conn.id,
                                                                connectionName: conn.name,
                                                                tableName:      tbl.name,
                                                                schemaName:     null,
                                                                columns:        tbl.columns || [],
                                                                foreignKeys:    tbl.foreign_keys || [],
                                                                primaryKeys:    tbl.primary_keys || []
                                                            }));
                                                            e.dataTransfer.effectAllowed = 'copy';
                                                        }}
                                                        onClick={() => onSelectTable({
                                                            connectionId:   conn.id,
                                                            connectionName: conn.name,
                                                            tableName:      tbl.name,
                                                            schemaName:     null,
                                                            tableType:      tbl.type,
                                                        })}
                                                        className={`flex-1 flex items-center gap-2 text-left cursor-grab active:cursor-grabbing border border-transparent`}
                                                    >
                                                        <DynamicIcon
                                                            name={tbl.type === 'view' ? 'Eye' : 'Table'}
                                                            className={`w-3 h-3 shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400'}`}
                                                        />
                                                        <span className="text-xs font-medium truncate">{tbl.name}</span>
                                                        {tbl.type === 'view' && (
                                                            <span className="ml-auto text-[9px] px-1 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-semibold">VIEW</span>
                                                        )}
                                                    </button>
                                                </div>
                                                
                                                {/* Columns List */}
                                                {isTableOpen && tbl.columns && (
                                                    <div className="ml-6 border-l border-slate-200 pl-2 mt-0.5 space-y-0.5">
                                                        {tbl.columns.map(col => (
                                                            <button
                                                                key={col.name}
                                                                draggable
                                                                onDragStart={(e) => {
                                                                    e.dataTransfer.setData('application/json', JSON.stringify({
                                                                        type: 'column',
                                                                        table: tbl.name,
                                                                        column: col.name,
                                                                        dataType: col.type,
                                                                        connectionId: conn.id,
                                                                        connectionName: conn.name
                                                                    }));
                                                                    e.dataTransfer.effectAllowed = 'copy';
                                                                }}
                                                                className="w-full flex items-center gap-2 px-1 py-1 rounded text-left hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors cursor-grab active:cursor-grabbing"
                                                            >
                                                                <DynamicIcon name={getColIcon(col.type)} className="w-[10px] h-[10px] text-slate-400 shrink-0" />
                                                                <span className="text-[10px] font-mono truncate">{col.name}</span>
                                                                {col.primary_key && <DynamicIcon name="Key" className="w-[10px] h-[10px] ml-auto text-amber-500 shrink-0" />}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {!loading && tables.length === 0 && (
                                        <p className="px-2 py-1.5 text-[10px] text-slate-400 italic">No tables found</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Add Connection Modal */}
            {showAddModal && (
                <AddConnectionModal
                    token={token}
                    onClose={() => setShowAddModal(false)}
                    onCreated={() => { loadConnections(); setShowAddModal(false); }}
                />
            )}
        </div>
    );
};

export default ConnectionsSidebar;
